import { fixed, trim } from '../../math/format';
import { nearestDrill } from '../../math/drills';
import {
  percentOfThread,
  pitchFromTpi,
  tapDrillInch,
  tapDrillMetric,
  threadDepth,
} from '../../math/threads';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

/** Common UN sizes: [name, major diameter, TPI]. */
const UN_SIZES: ReadonlyArray<readonly [string, number, number]> = [
  ['#10-24', 0.19, 24],
  ['#10-32', 0.19, 32],
  ['1/4-20', 0.25, 20],
  ['1/4-28', 0.25, 28],
  ['5/16-18', 0.3125, 18],
  ['5/16-24', 0.3125, 24],
  ['3/8-16', 0.375, 16],
  ['3/8-24', 0.375, 24],
  ['1/2-13', 0.5, 13],
  ['1/2-20', 0.5, 20],
  ['5/8-11', 0.625, 11],
  ['3/4-10', 0.75, 10],
];

/** Common metric sizes: [name, major diameter mm, pitch mm]. */
const METRIC_SIZES: ReadonlyArray<readonly [string, number, number]> = [
  ['M4×0.7', 4, 0.7],
  ['M5×0.8', 5, 0.8],
  ['M6×1.0', 6, 1.0],
  ['M8×1.25', 8, 1.25],
  ['M10×1.5', 10, 1.5],
  ['M12×1.75', 12, 1.75],
];

export const THREAD_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'threads.pitch',
    topicId: 'threads',
    skill: 'Pitch from TPI',
    gen: (rng) => {
      const [name, , tpi] = rng.pick(UN_SIZES);
      const solved = pitchFromTpi(tpi);

      return {
        kind: 'mc',
        prompt: `A ${name} thread. What is the pitch?`,
        sub: 'Pitch is the distance from one crest to the next.',
        formula: 'pitch = 1 ÷ TPI',
        why: `1 ÷ ${tpi} = ${fixed(solved.value, 4)}". Pitch and TPI are the same fact said two ways.`,
        steps: solved.steps,
        ...mcOptions(rng, `${fixed(solved.value, 4)}"`, [
          {
            // /1000 rather than /100: at 10 TPI the /100 version lands on
            // 0.100", which is the correct answer wearing a disguise.
            label: `${fixed(tpi / 1000, 4)}"`,
            value: tpi / 1000,
            trap: 'You shifted the decimal point instead of dividing into one. Pitch is 1 ÷ TPI.',
          },
          {
            label: `${fixed(solved.value * 2, 4)}"`,
            value: solved.value * 2,
            trap: 'Double the pitch. That is the lead of a two-start thread, not the pitch of this one.',
          },
          {
            label: `${fixed(tpi, 4)}"`,
            value: tpi,
            trap: 'That is the thread count itself, not a distance. TPI counts threads in an inch.',
          },
        ]),
      };
    },
  },
  {
    id: 'threads.tapDrill',
    topicId: 'threads',
    skill: 'Tap drill for an inch thread',
    gen: (rng) => {
      const [name, major, tpi] = rng.pick(UN_SIZES);
      const solved = tapDrillInch(major, tpi);
      const correct = solved.value.drill.drill;
      // A drill a couple of steps either side, which would leave the thread
      // too weak or the tap fighting for its life.
      const tooBig = nearestDrill(solved.value.size + 0.02);
      const tooSmall = nearestDrill(solved.value.size - 0.02);

      return {
        kind: 'mc',
        prompt: `Tapping ${name} at 75% of thread. Which drill?`,
        formula: 'tap drill = major Ø − (1.299 × %thread ÷ 100) ÷ TPI',
        why: `${trim(major, 4)} − ${fixed(1.299 * 0.75 / tpi, 4)} = ${fixed(solved.value.size, 4)}", so the ${correct.label} at ${fixed(correct.inches, 4)}".`,
        steps: solved.steps,
        ...mcOptions(rng, correct.label, [
          {
            label: tooBig.drill.label,
            value: tooBig.drill.inches,
            trap: 'Too big. That leaves barely any thread for the bolt to grip.',
          },
          {
            label: tooSmall.drill.label,
            value: tooSmall.drill.inches,
            trap: 'Too small. The tap has to cut far more metal, and that is how taps snap off in a part.',
          },
          {
            label: `${trim(major, 4)}"`,
            value: major,
            trap: 'That is the major diameter — the size of the bolt. Drill that and there is no thread at all.',
          },
        ]),
      };
    },
  },
  {
    id: 'threads.metricTapDrill',
    topicId: 'threads',
    skill: 'Tap drill for a metric thread',
    gen: (rng) => {
      const [name, major, pitch] = rng.pick(METRIC_SIZES);
      const solved = tapDrillMetric(major, pitch);
      // The shop rule of thumb: major minus pitch. It is what the chart says.
      const rule = major - pitch;

      return {
        kind: 'mc',
        prompt: `Tapping ${name}. Roughly what drill, in millimetres?`,
        formula: 'tap drill ≈ major Ø − pitch',
        why: `${trim(major, 2)} − ${trim(pitch, 2)} = ${fixed(rule, 2)} mm. That rule of thumb lands within a hair of the exact ${fixed(solved.value.size, 2)} mm.`,
        steps: solved.steps,
        ...mcOptions(rng, `${fixed(rule, 2)} mm`, [
          {
            label: `${fixed(major + pitch, 2)} mm`,
            value: major + pitch,
            trap: 'You added. The hole has to be smaller than the bolt or there is nothing to cut a thread into.',
          },
          {
            label: `${fixed(major, 2)} mm`,
            value: major,
            trap: 'That is the bolt diameter. Drill that and the tap has nothing to bite.',
          },
          {
            label: `${fixed(major - pitch * 2, 2)} mm`,
            value: major - pitch * 2,
            trap: 'Subtracted the pitch twice. That hole is far too tight and will break the tap.',
          },
        ]),
      };
    },
  },
  {
    id: 'threads.depth',
    topicId: 'threads',
    skill: 'Single depth of thread',
    gen: (rng) => {
      const [name, , tpi] = rng.pick(UN_SIZES);
      const solved = threadDepth(tpi);

      return {
        kind: 'numeric',
        prompt: `Single-pointing a ${name} thread. How deep does the tool go?`,
        sub: 'Single depth, in inches. Four places.',
        answer: solved.value,
        tolerance: 0.0005,
        unit: '"',
        places: 4,
        formula: 'single depth = 0.6134 ÷ TPI',
        why: `0.6134 ÷ ${tpi} = ${fixed(solved.value, 4)}". That is depth on one flank, which is what the cross-slide travels.`,
        steps: solved.steps,
      };
    },
  },
  {
    id: 'threads.percent',
    topicId: 'threads',
    skill: 'Percent of thread from a drill',
    gen: (rng) => {
      const [name, major, tpi] = rng.pick(UN_SIZES);
      const proper = tapDrillInch(major, tpi).value.drill.drill;
      // Pick a drill one or two steps off and ask what it actually gives.
      const offset = rng.pick([-0.008, -0.004, 0.004, 0.008]);
      const used = nearestDrill(proper.inches + offset).drill;
      const solved = percentOfThread(major, tpi, used.inches);
      const rounded = Math.round(solved.value / 5) * 5;

      return {
        kind: 'mc',
        prompt: `You grabbed the ${used.label} for a ${name}. Roughly what percent of thread does that give?`,
        formula: '%thread = (major Ø − drill) × 100 × TPI ÷ 1.299',
        why: `(${trim(major, 4)} − ${fixed(used.inches, 4)}) × 100 × ${tpi} ÷ 1.299 = ${fixed(solved.value, 0)}%. ${
          solved.value > 78
            ? 'That is more engagement than you need, and a lot more torque on the tap.'
            : solved.value < 70
              ? 'That is on the light side — easy tapping, but less thread holding the bolt.'
              : 'Right in the useful range.'
        }`,
        steps: solved.steps,
        ...mcOptions(rng, `About ${rounded}%`, [
          {
            label: `About ${rounded + 20}%`,
            value: rounded + 20,
            trap: `Work it out rather than guessing: that drill gives about ${fixed(solved.value, 0)}%.`,
          },
          {
            label: `About ${Math.max(5, rounded - 20)}%`,
            value: Math.max(5, rounded - 20),
            trap: `Work it out rather than guessing: that drill gives about ${fixed(solved.value, 0)}%.`,
          },
          {
            label: '100%',
            value: 100,
            trap: 'Full engagement needs a hole the size of the minor diameter, and roughly doubles the tapping torque for almost no extra strength.',
          },
        ]),
      };
    },
  },
  {
    id: 'threads.tpiMeaning',
    topicId: 'threads',
    skill: 'What TPI counts',
    gen: (rng) => {
      const [name, , tpi] = rng.pick(UN_SIZES);
      // 1" is excluded: at that length the answer equals the TPI itself, which
      // collapses two distractors onto the correct option.
      const length = rng.pick([0.5, 1.5, 2.0, 2.5]);
      const count = tpi * length;

      return {
        kind: 'mc',
        prompt: `How many full threads are there in ${trim(length, 2)}" of a ${name}?`,
        formula: 'threads = TPI × length',
        why: `TPI counts threads in one inch, so ${tpi} × ${trim(length, 2)} = ${trim(count, 1)}.`,
        steps: [
          { kind: 'formula', text: 'threads = TPI × length' },
          { kind: 'substitute', text: `${tpi} × ${trim(length, 2)}` },
          { kind: 'result', text: `= ${trim(count, 1)} threads` },
          {
            kind: 'note',
            text: 'TPI is a count per inch, not a distance. The distance is the pitch, which is 1 ÷ TPI.',
          },
        ],
        ...mcOptions(rng, `${trim(count, 1)}`, [
          {
            label: `${trim(tpi / length, 1)}`,
            value: tpi / length,
            trap: 'You divided. More length means more threads, so this has to be a multiplication.',
          },
          {
            label: `${trim(length / tpi, 4)}`,
            value: length / tpi,
            trap: 'That is a distance in inches, not a count of threads.',
          },
          {
            label: `${tpi}`,
            value: tpi,
            trap: `That is the count in one inch. The question asks about ${trim(length, 2)}".`,
          },
        ]),
      };
    },
  },
];
